// frontend/packages/business-console/src/hooks/useCloudinaryUploadWidget.js

import { useCallback, useEffect, useRef, useState } from 'react';
import { getCloudinaryUploadSignature } from '../services/mediaService';

const WIDGET_SRC = 'https://widget.cloudinary.com/v2.0/global/all.js';

function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('No window'));
      return;
    }

    if (window.cloudinary?.createUploadWidget) {
      resolve(true);
      return;
    }

    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Cloudinary widget'))
      );
      return;
    }

    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error('Failed to load Cloudinary widget'));
    document.head.appendChild(s);
  });
}

function normalizeTags(tags) {
  if (Array.isArray(tags)) {
    return tags.map((t) => String(t).trim()).filter(Boolean);
  }

  if (typeof tags === 'string') {
    return tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }

  return [];
}

export default function useCloudinaryUploadWidget() {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const loadingRef = useRef(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (loadingRef.current) return;
      loadingRef.current = true;

      try {
        await loadScriptOnce(WIDGET_SRC);
        if (alive) setReady(true);
      } catch (e) {
        if (alive) setError(e?.message || String(e));
      } finally {
        loadingRef.current = false;
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const openWidget = useCallback(
    async ({ intent, sources, onSuccess, onError }) => {
      if (!window.cloudinary?.createUploadWidget) {
        const msg = 'Cloudinary widget is not loaded.';
        onError?.(new Error(msg));
        throw new Error(msg);
      }

      const cloudName = String(intent?.cloudName || '').trim();
      const apiKey = String(intent?.apiKey || '').trim();
      const params = intent?.params || {};

      const uploadPreset = String(params.upload_preset || '').trim();
      const publicId = String(params.public_id || '').trim();
      const folder = String(params.folder || '').trim() || undefined;
      const tags = normalizeTags(params.tags);
      const resourceType =
        String(params.resource_type || 'image')
          .trim()
          .toLowerCase() || 'image';

      if (!cloudName || !apiKey || !publicId) {
        const msg =
          'Invalid upload intent payload (missing cloudName, apiKey, or public_id).';
        onError?.(new Error(msg));
        throw new Error(msg);
      }

      if (!uploadPreset) {
        const msg =
          'Cloudinary upload preset is not configured. Set CLOUDINARY_UPLOAD_PRESET on the backend and redeploy.';
        onError?.(new Error(msg));
        throw new Error(msg);
      }

      const uploadSignatureFn = async (callback, paramsToSign) => {
        try {
          const resp = await getCloudinaryUploadSignature(paramsToSign);
          const signature = String(resp?.signature || '').trim();

          if (!signature) {
            throw new Error('Signature endpoint returned no signature.');
          }

          callback(signature);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('[CloudinaryWidget] signature error', e);
          onError?.(e);
        }
      };

      const widget = window.cloudinary.createUploadWidget(
        {
          cloudName,
          apiKey,

          // Dynamic signing only
          uploadSignature: uploadSignatureFn,

          // Widget/client options use camelCase
          uploadPreset,
          publicId,
          folder,
          tags,
          resourceType,

          // Image and video formats only (no PDF / SVG). The backend enforces
          // the same list (services/media/cloudinary/allowedFormats.js); this
          // just stops the picker offering files that would be refused.
          clientAllowedFormats:
            resourceType === 'video'
              ? ['mp4', 'mov', 'webm', 'm4v']
              : ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'heic', 'heif'],

          sources:
            Array.isArray(sources) && sources.length
              ? sources
              : ['local', 'camera', 'url'],

          multiple: false,
          showAdvancedOptions: false,
          showCompletedButton: true,
          showUploadMoreButton: false,
          singleUploadAutoClose: false,
        },
        (err, result) => {
          if (err) {
            // eslint-disable-next-line no-console
            console.error('[CloudinaryWidget] error:', err);
            onError?.(err);
            return;
          }

          if (!result) return;

          if (result.event === 'success') {
            onSuccess?.(result.info || {});
          }
        }
      );

      widget.open();
      return widget;
    },
    []
  );

  return { ready, error, openWidget };
}
